import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeatureContext = createContext(null);

// Default features (used before API response)
const DEFAULT_FEATURES = {
  sections: {
    overview: { enabled: true, label: "Overview" },
    projects: { enabled: true, label: "Projects" },
    procurement: { enabled: true, label: "Procurement" },
    resources: { enabled: true, label: "Resources" },
    finance: { enabled: false, label: "Finance" },
    customer_service: { enabled: true, label: "Customer Service" },
    rentals: { enabled: true, label: "Rentals" },
    admin: { enabled: true, label: "Admin" },
  },
  menu_items: {
    dashboard: { enabled: true, label: "Dashboard", section: "overview" },
    my_assignments: { enabled: true, label: "My Assignments", section: "overview" },
    job_history: { enabled: true, label: "Job History", section: "overview" },
    pending_approvals: { enabled: true, label: "Pending Approvals", section: "overview" },
    projects: { enabled: true, label: "Projects", section: "projects" },
    operations: { enabled: true, label: "Operations", section: "projects" },
    parts: { enabled: true, label: "Parts", section: "projects" },
    part_categories: { enabled: true, label: "Part Categories", section: "projects" },
    labour: { enabled: true, label: "Labour", section: "projects" },
    suppliers: { enabled: true, label: "Suppliers", section: "procurement" },
    purchase_orders: { enabled: true, label: "Purchase Orders", section: "procurement" },
    materials: { enabled: true, label: "Materials", section: "procurement" },
    inventory: { enabled: true, label: "Inventory", section: "procurement" },
    standard_items: { enabled: true, label: "Standard Items", section: "resources" },
    team: { enabled: true, label: "Team", section: "resources" },
    expenses: { enabled: false, label: "Expenses", section: "finance" },
    services: { enabled: true, label: "Services", section: "customer_service" },
    installation_systems: { enabled: true, label: "Installation Systems", section: "customer_service" },
    customers: { enabled: true, label: "Customers", section: "customer_service" },
    service_calendar: { enabled: true, label: "Service Calendar", section: "customer_service" },
    add_customer_service: { enabled: true, label: "Add Customer Service", section: "customer_service" },
    rental_items: { enabled: true, label: "Rental Items", section: "rentals" },
    rental_management: { enabled: true, label: "Rental Management", section: "rentals" },
    user_management: { enabled: true, label: "User Management", section: "admin" },
    settings: { enabled: true, label: "Settings", section: "admin" },
  },
  modules: {
    ai_stock_controller: { enabled: true, label: "AI Stock Controller" },
    customer_portal: { enabled: true, label: "Customer Portal" },
    rental_system: { enabled: true, label: "Rental System" },
    service_booking: { enabled: true, label: "Service Booking" },
    purchase_orders: { enabled: true, label: "Purchase Orders" },
  },
};

// Default role permissions (all false for worker/manager)
const DEFAULT_ROLE_PERMISSIONS = {
  worker: {
    sections: {},
    menu_items: {},
    modules: {},
  },
  manager: {
    sections: {},
    menu_items: {},
    modules: {},
  },
};

export const FeatureProvider = ({ children }) => {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [rolePermissions, setRolePermissions] = useState(DEFAULT_ROLE_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(null);

  const fetchFeatures = async () => {
    try {
      const [featuresRes, permissionsRes, userRes] = await Promise.all([
        axios.get(`${API}/settings/features`, { withCredentials: true }),
        axios.get(`${API}/settings/role-permissions`, { withCredentials: true }).catch(() => ({ data: DEFAULT_ROLE_PERMISSIONS })),
        axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setFeatures(featuresRes.data);
      setRolePermissions(permissionsRes.data || DEFAULT_ROLE_PERMISSIONS);
      if (userRes.data?.role) {
        setCurrentRole(userRes.data.role);
      }
    } catch (error) {
      console.log("Using default features");
      setFeatures(DEFAULT_FEATURES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const updateFeatures = async (updates) => {
    try {
      const response = await axios.put(`${API}/settings/features`, updates, {
        withCredentials: true,
      });
      setFeatures(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resetFeatures = async () => {
    try {
      const response = await axios.post(`${API}/settings/features/reset`, {}, {
        withCredentials: true,
      });
      setFeatures(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateRolePermissions = async (role, updates) => {
    try {
      const response = await axios.put(`${API}/settings/role-permissions/${role}`, updates, {
        withCredentials: true,
      });
      setRolePermissions(prev => ({
        ...prev,
        [role]: response.data,
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resetRolePermissions = async () => {
    try {
      const response = await axios.post(`${API}/settings/role-permissions/reset`, {}, {
        withCredentials: true,
      });
      setRolePermissions(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Check if a section is enabled (global + role-based)
  const isSectionEnabled = (sectionKey, role = currentRole) => {
    // Global feature check first
    const globalEnabled = features.sections?.[sectionKey]?.enabled ?? true;
    if (!globalEnabled) return false;
    
    // Admin always has access
    if (role === "admin") return true;
    
    // Check role permissions for worker/manager
    if (role === "worker" || role === "manager") {
      const rolePerms = rolePermissions[role];
      if (rolePerms?.sections) {
        return rolePerms.sections[sectionKey] ?? false;
      }
      return false; // Default to no access
    }
    
    // Other roles (customer) - return global setting
    return globalEnabled;
  };

  // Check if a menu item is enabled (global + role-based)
  const isMenuItemEnabled = (itemKey, role = currentRole) => {
    const item = features.menu_items?.[itemKey];
    if (!item) return true;
    
    // Global feature check first
    if (!item.enabled) return false;
    
    // Check if section is enabled
    const sectionKey = item.section;
    const sectionEnabled = features.sections?.[sectionKey]?.enabled ?? true;
    if (!sectionEnabled) return false;
    
    // Admin always has access
    if (role === "admin") return true;
    
    // Check role permissions for worker/manager
    if (role === "worker" || role === "manager") {
      // First check section permission
      const rolePerms = rolePermissions[role];
      if (rolePerms?.sections && !rolePerms.sections[sectionKey]) {
        return false;
      }
      // Then check menu item permission
      if (rolePerms?.menu_items) {
        return rolePerms.menu_items[itemKey] ?? false;
      }
      return false; // Default to no access
    }
    
    // Other roles - return global setting
    return item.enabled && sectionEnabled;
  };

  // Check if a module is enabled (global + role-based)
  const isModuleEnabled = (moduleKey, role = currentRole) => {
    // Global feature check first
    const globalEnabled = features.modules?.[moduleKey]?.enabled ?? true;
    if (!globalEnabled) return false;
    
    // Admin always has access
    if (role === "admin") return true;
    
    // Check role permissions for worker/manager
    if (role === "worker" || role === "manager") {
      const rolePerms = rolePermissions[role];
      if (rolePerms?.modules) {
        return rolePerms.modules[moduleKey] ?? false;
      }
      return false; // Default to no access
    }
    
    // Other roles - return global setting
    return globalEnabled;
  };

  const value = {
    features,
    rolePermissions,
    loading,
    currentRole,
    setCurrentRole,
    updateFeatures,
    resetFeatures,
    updateRolePermissions,
    resetRolePermissions,
    refreshFeatures: fetchFeatures,
    isSectionEnabled,
    isMenuItemEnabled,
    isModuleEnabled,
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error("useFeatures must be used within a FeatureProvider");
  }
  return context;
};

export default FeatureContext;
